import React, { useCallback, useEffect, useMemo, useRef } from 'react';
// Deep import of the Grid wrapper only (not the `../../ui` or `../../ui/components`
// barrels, which eagerly require mapbox-gl and crash in jsdom — see the import
// comment in PmlSnapshotPage.jsx). `src/ui/components/Grid/index.js` only re-exports
// Grid + AlpineThemeGrid, so importing it directly skips that chain entirely.
import { AlpineThemeGrid } from '../../ui/components/Grid';
import { formatUsd, STATUS_COLORS } from './hexMapStyles';

const isNil = (v) => v === null || v === undefined;
const formatPct = (v) => (isNil(v) ? '—' : `${Math.round(Number(v))}%`);
const formatScale = (v) => (isNil(v) ? '—' : `${v} mi`);

// `concentration` is the verbatim nested-JSONB object from the artifact contract:
// `{top_dealer, top_dealer_tiv, top_dealer_share_pct, util_ladder, shape,
// ratio_5_50}` — may be null (only 5-mi rows carry it). `concernRowData` narrows
// it to just the two fields this column renders; `formatTopDealer` turns that
// into "James Wood Motors (100.0%)" (name only if share is missing, "—" if
// concentration itself is null). Exported so the exact rendered string is
// unit-testable without a grid.
export function formatTopDealer(v) {
  if (isNil(v) || isNil(v.name)) return '—';
  if (isNil(v.sharePct)) return v.name;
  return `${v.name} (${Number(v.sharePct).toFixed(1)}%)`;
}

const STATUS_FALLBACK_COLOR = '#8A94A6';

const statusCellRenderer = (params) => {
  const color = STATUS_COLORS[params.value] || STATUS_FALLBACK_COLOR;
  const label = params.value || '—';
  return (
    <span
      style={{
        background: color,
        color: '#fff',
        padding: '2px 8px',
        borderRadius: 10,
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'uppercase',
      }}
    >
      {label}
    </span>
  );
};

// One register row per concerning cell. Per the artifact contract
// (2026-07-22-artifact-contract.md), the real cap for a row comes from the
// page-level ceiling ladder — `ceilingLadder.caps_by_scale_mi[String(scaleMi)]`
// (keys are strings) — not from any per-cell occ/utilization math. `over` is the
// aggregate PML's overage against that cap: `pml250Agg - cap` (agg basis, same
// basis `utilization` is already computed on). Exported so this derivation is
// unit-testable without a grid.
export function concernRowData(concerns, ceilingLadder) {
  const capsByScaleMi = ceilingLadder?.caps_by_scale_mi || {};
  return (concerns || []).map((c) => {
    const rawCap = capsByScaleMi[String(c.scaleMi)];
    const cap = isNil(rawCap) ? null : Number(rawCap);
    const over = isNil(cap) || isNil(c.pml250Agg) ? null : Number(c.pml250Agg) - cap;
    return {
      cellId: c.cellId,
      scaleMi: c.scaleMi,
      place: c.place,
      status: c.status,
      utilization: c.utilization,
      occ: c.pml250Occ,
      agg: c.pml250Agg,
      cap,
      over,
      topDealer: c.concentration ? { name: c.concentration.top_dealer, sharePct: c.concentration.top_dealer_share_pct } : null,
    };
  });
}

export function top10RowData(top10) {
  return (top10 || []).map((c) => ({
    cellId: c.cellId,
    scaleMi: c.scaleMi,
    nLocations: c.nLocations,
    tiv: c.tiv,
    aal: c.aal,
    pml: c.pml250Agg,
    utilization: c.utilization,
    status: c.status,
  }));
}

const S = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 16 },
  sectionTitle: { fontWeight: 600, fontSize: 14, color: '#212B36' },
};

// Two AG Grid tables (via the AlpineThemeGrid wrapper): the concern register and
// the top-10 cells by PML. The wrapper doesn't expose a row-click prop, so both
// grids wire clicks themselves through the grid API's `rowClicked` event (grabbed
// via `setGridReference`) rather than needing a wrapper change.
//
// `section` selects which table(s) to render so the page can put each on its own
// tab: 'register' or 'top10' renders just that one; omitted (the default) renders
// both stacked, preserving the original single-component behavior.
const ConcernRegister = ({ concerns, top10, ceilingLadder, onCellClick, section }) => {
  const showRegister = section == null || section === 'register';
  const showTop10 = section == null || section === 'top10';
  const onCellClickRef = useRef(onCellClick);
  useEffect(() => {
    onCellClickRef.current = onCellClick;
  }, [onCellClick]);

  const registerRowData = useMemo(() => concernRowData(concerns, ceilingLadder), [concerns, ceilingLadder]);
  const top10Rows = useMemo(() => top10RowData(top10), [top10]);

  const registerColumnDefs = useMemo(
    () => [
      { field: 'place', headerName: 'Place' },
      { field: 'scaleMi', headerName: 'Scale', valueFormatter: (p) => formatScale(p.value) },
      { field: 'status', headerName: 'Status', cellRenderer: statusCellRenderer },
      { field: 'utilization', headerName: 'Util', valueFormatter: (p) => formatPct(p.value) },
      { field: 'occ', headerName: 'OCC', valueFormatter: (p) => formatUsd(p.value) },
      { field: 'agg', headerName: 'AGG', valueFormatter: (p) => formatUsd(p.value) },
      { field: 'cap', headerName: 'Cap', valueFormatter: (p) => formatUsd(p.value) },
      { field: 'over', headerName: 'Over', valueFormatter: (p) => formatUsd(p.value) },
      { field: 'topDealer', headerName: 'Top dealer', valueFormatter: (p) => formatTopDealer(p.value) },
    ],
    []
  );

  const top10ColumnDefs = useMemo(
    () => [
      { field: 'cellId', headerName: 'Hex' },
      { field: 'scaleMi', headerName: 'Scale', valueFormatter: (p) => formatScale(p.value) },
      { field: 'nLocations', headerName: 'Locations' },
      { field: 'tiv', headerName: 'TIV', valueFormatter: (p) => formatUsd(p.value) },
      { field: 'aal', headerName: 'AAL', valueFormatter: (p) => formatUsd(p.value) },
      { field: 'pml', headerName: '1-in-250 PML', valueFormatter: (p) => formatUsd(p.value) },
      { field: 'utilization', headerName: 'Util', valueFormatter: (p) => formatPct(p.value) },
      { field: 'status', headerName: 'Status', cellRenderer: statusCellRenderer },
    ],
    []
  );

  const attachRowClick = useCallback((params) => {
    params.api.addEventListener('rowClicked', (event) => {
      const row = event.data;
      if (row && onCellClickRef.current) onCellClickRef.current(row.scaleMi, row.cellId);
    });
  }, []);

  return (
    <div data-testid="concern-register" style={S.wrap}>
      {showRegister && (
        <div>
          <div style={S.sectionTitle}>Concern register</div>
          <AlpineThemeGrid setGridReference={attachRowClick} columnDefs={registerColumnDefs} rowData={registerRowData} headerBgColor="light-gray" />
        </div>
      )}
      {showTop10 && (
        <div>
          <div style={S.sectionTitle}>Top 10 cells</div>
          <AlpineThemeGrid setGridReference={attachRowClick} columnDefs={top10ColumnDefs} rowData={top10Rows} headerBgColor="light-gray" />
        </div>
      )}
    </div>
  );
};

export default ConcernRegister;
